import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { useCategories } from "@contexts/CategoriesContext";
import { useModals } from "@contexts/ModalsContext";
import Icon from "@components/icon";
import styles from "./SelectCategory.module.css";
import Button from "@components/button";

const ROOT_PARENT_KEY = "__root__";

const isRootParent = (parentId) => (
  parentId === null
  || parentId === undefined
  || parentId === 0
  || parentId === "0"
);

const getParentKey = (parentId) => (
  isRootParent(parentId)
    ? ROOT_PARENT_KEY
    : String(parentId)
);

const CategorySelectionModal = ({
  categories,
  selectedId,
  initialCategoryId,
  onSelect,
  onClose,
}) => {
  const categoriesById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(String(category.id), category);
    });
    return map;
  }, [categories]);

  const categoriesByParent = useMemo(() => {
    const map = new Map();
    const appendToParent = (parentId, category) => {
      const key = getParentKey(parentId);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(category);
    };

    categories.forEach((category) => {
      appendToParent(category.parentCategoryId, category);
    });

    map.forEach((list) => {
      list.sort((a, b) => a.name.localeCompare(b.name));
    });

    return map;
  }, [categories]);

  const initialPath = useMemo(() => {
    if (!initialCategoryId) {
      return [];
    }

    const path = [];
    let current = categoriesById.get(String(initialCategoryId));

    while (current && !isRootParent(current.parentCategoryId)) {
      path.unshift(current.parentCategoryId);
      current = categoriesById.get(String(current.parentCategoryId));
    }

    return path;
  }, [categoriesById, initialCategoryId]);

  const [activePath, setActivePath] = useState(initialPath);

  useEffect(() => {
    setActivePath(initialPath);
  }, [initialPath]);

  const currentParentId = activePath.length > 0 ? activePath[activePath.length - 1] : null;
  const currentParent = currentParentId !== null
    ? categoriesById.get(String(currentParentId))
    : null;

  const getChildren = useCallback((parentId) => (
    categoriesByParent.get(getParentKey(parentId)) || []
  ), [categoriesByParent]);

  const visibleCategories = useMemo(() => (
    getChildren(currentParentId)
  ), [currentParentId, getChildren]);

  const onCategoryClick = useCallback((category) => {
    const children = getChildren(category.id);
    if (children.length > 0) {
      setActivePath((prev) => ([
        ...prev,
        category.id,
      ]));
    } else {
      onSelect(category.id);
    }
  }, [getChildren, onSelect]);

  const onBreadcrumbClick = useCallback((index) => {
    setActivePath((prev) => prev.slice(0, index));
  }, []);

  const onBack = useCallback(() => {
    setActivePath((prev) => prev.slice(0, -1));
  }, []);

  const onSelectCurrent = useCallback(() => {
    if (currentParentId !== null) {
      onSelect(currentParentId);
    }
  }, [currentParentId, onSelect]);

  const breadcrumbs = useMemo(() => {
    const items = [{ id: null, label: "Categorie" }];
    activePath.forEach((categoryId) => {
      const category = categoriesById.get(String(categoryId));
      if (category) {
        items.push({
          id: categoryId,
          label: category.name,
        });
      }
    });
    return items;
  }, [activePath, categoriesById]);

  return (
    <div className={styles.modalContent}>
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={item.id ?? "root"}>
            {index > 0 && (
              <span className={styles.breadcrumbSeparator}>/</span>
            )}
            <button
              type="button"
              className={styles.breadcrumbButton}
              onClick={() => onBreadcrumbClick(index)}
            >
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className={styles.categoryList}>
        {visibleCategories.length === 0 ? (
          <div className={styles.emptyState}>
            <span>Nessuna sottocategoria disponibile.</span>
            {currentParent && (
              <button
                type="button"
                className={styles.actionButton}
                onClick={onSelectCurrent}
              >
                Seleziona &quot;{currentParent.name}&quot;
              </button>
            )}
          </div>
        ) : (
          visibleCategories.map((category) => {
            const children = getChildren(category.id);
            const hasChildren = children.length > 0;
            const isSelected = selectedId !== null
              && selectedId !== undefined
              && String(selectedId) === String(category.id);

            return (
              <button
                type="button"
                key={category.id}
                className={[
                  styles.categoryItem,
                  hasChildren ? styles.hasChildren : null,
                  isSelected ? styles.selected : null,
                ].filter(Boolean).join(" ")}
                onClick={() => onCategoryClick(category)}
              >
                <Icon
                  name={hasChildren ? "chevron_right" : ""}
                  className={styles.categoryIcon}
                  fill={0}
                  weight={hasChildren ? 200 : 400}
                  opticalSize={24}
                />
                <span className={styles.categoryName}>{category.name}</span>
              </button>
            );
          })
        )}
      </div>

      <div className={styles.footer}>
        {currentParent && (
        <Button
          color="secondary"
          text="Indietro"
          icon="chevron_left"
          onClick={onBack}
        />)}

        <Button
          className={styles.right}
          color="primary"
          text="Cerca"
          icon="search"
          onClick={onSelectCurrent}
        />
      </div>
    </div>
  );
};

CategorySelectionModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    parentCategoryId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  })).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CategorySelectionModal.defaultProps = {
  selectedId: null,
  initialCategoryId: null,
};

const SelectCategory = ({ value, setValue, disabled }) => {
  const { categories } = useCategories();
  const { openModal, closeModal } = useModals();

  const selectedCategory = useMemo(() => (
    categories.find((category) => String(category.id) === String(value)) || null
  ), [categories, value]);

  const containerClassName = useMemo(() => ([
    styles.selectCategory,
    selectedCategory ? styles.filled : null,
    disabled ? styles.disabled : null,
  ].filter(Boolean).join(" ")), [selectedCategory, disabled]);

  const onClearSelection = useCallback((event) => {
    event.stopPropagation();
    setValue(null);
  }, [setValue]);

  const onOpenModal = useCallback(() => {
    if (disabled) {
      return;
    }

    let modalId = null;

    const handleSelect = (newValue) => {
      setValue(newValue);
      closeModal(modalId);
    };

    const handleClose = () => {
      closeModal(modalId);
    };

    modalId = openModal({
      title: "Seleziona categoria",
      children: (
        <CategorySelectionModal
          categories={categories}
          selectedId={value}
          initialCategoryId={value}
          onSelect={handleSelect}
          onClose={handleClose}
        />
      ),
    });
  }, [categories, closeModal, disabled, openModal, setValue, value]);

  const displayText = selectedCategory?.name || "";

  return (
    <div className={containerClassName}>
      <label>Categoria</label>
      <button
        type="button"
        className={styles.trigger}
        onClick={onOpenModal}
        disabled={disabled}
      >
        <span className={selectedCategory ? styles.triggerText : styles.placeholder}>
          {displayText}
        </span>
      </button>
      {!disabled && selectedCategory && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={onClearSelection}
          aria-label="Deseleziona categoria"
        >
          <Icon
            name="close"
            fill={0}
            weight={300}
            grade={0}
            opticalSize={20}
            size={20}
          />
        </button>
      )}
      <Icon
        name="expand_more"
        className={styles.arrowIcon}
        fill={0}
        weight={300}
        grade={0}
        opticalSize={24}
      />
    </div>
  );
};

SelectCategory.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SelectCategory.defaultProps = {
  value: null,
  disabled: false,
};

export default SelectCategory;
